export default function PlayerEditor() {
  return (
    <section id="player-editor">
      <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">Player Editor</h2>
      <p className="text-slate-600 dark:text-slate-400 mb-8 leading-relaxed">
        The Player Editor lets you view and modify data for every player who has ever spawned in the
        world. A list of players is shown on the left side — select any player to start editing.
        The editor is split into sub-tabs for different types of player data.
      </p>

      {/* Player List & Sidebar */}
      <div id="player-list" className="mb-8">
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Player List & Sidebar</h3>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5 space-y-3 text-sm text-slate-600 dark:text-slate-400">
          <p>
            The player list on the left side of the screen shows all players who have saved data in the
            world. If nobody has ever joined the world (e.g. a freshly created world that hasn't been
            loaded in Minecraft yet), there may be no player data to show.
          </p>
          <ul className="space-y-2">
            <li>
              <strong className="text-slate-800 dark:text-slate-200">Player Names</strong> — Minecraft
              Bedrock doesn't always store player names in the world database, so players may appear as
              their internal ID. You can double-click any player in the list to set a custom display name.
              Alternatively, install the{' '}
              <a href="https://mygen.co.uk/project/playername-tagger" target="_blank" rel="noopener noreferrer" className="text-primary-500 hover:text-primary-600 underline">
                PlayerName Tagger
              </a>{' '}
              addon — a free Bedrock behaviour pack that automatically writes player names to the world
              data as they join, so they always show up correctly.
            </li>
            <li>
              <strong className="text-slate-800 dark:text-slate-200">Delete Player</strong> — Remove a
              player's data from the world entirely. Deleted players are visually marked in the list but
              not actually removed until you save. You can undo a pending deletion before saving.
            </li>
            <li>
              <strong className="text-slate-800 dark:text-slate-200">Search Players</strong> — Search
              across all players for items, tags, and dynamic properties in one place. Results show
              the player name, location, and a description of the match. Selecting a result switches
              to the relevant player and opens the appropriate tab — Inventory for item matches, or
              Properties & Tags for tag and property matches.
            </li>
          </ul>
        </div>
      </div>

      {/* General */}
      <div id="player-general" className="mb-8">
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">General</h3>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5 space-y-3 text-sm text-slate-600 dark:text-slate-400">
          <p>Core player properties organized into groups.</p>

          <div>
            <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-2">Location</h4>
            <ul className="space-y-1">
              <li><strong>Position X / Y / Z</strong> — The player's exact coordinates. Change these to teleport a player.</li>
              <li><strong>Dimension</strong> — Which dimension the player is in: Overworld, Nether, or The End.</li>
            </ul>
          </div>

          <div>
            <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-2">Survival</h4>
            <ul className="space-y-1">
              <li><strong>Health</strong> — The player's health points. 20 = full health (10 hearts). Shown with a heart indicator.</li>
              <li><strong>Air</strong> — Oxygen level (0–300). Full air is 300. Decreases while underwater.</li>
              <li><strong>Food Level</strong> — Hunger bar value (0–20). 20 = fully fed.</li>
              <li><strong>Food Saturation</strong> — Hidden hunger buffer. Higher saturation means the hunger bar drains more slowly.</li>
              <li><strong>Experience Level</strong> — The player's XP level.</li>
            </ul>
          </div>

          <div>
            <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-2">Game State</h4>
            <ul className="space-y-1">
              <li><strong>Game Mode</strong> — The player's individual game mode: Default (uses world setting), Survival, Creative, Adventure, or Spectator.</li>
            </ul>
          </div>

          <div>
            <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-2">Player Abilities</h4>
            <p>The same ability checkboxes as in the Level Editor's Default Abilities tab, but applied per-player. Lets you give individual players different permissions.</p>
          </div>
        </div>
      </div>

      {/* Inventory */}
      <div id="player-inventory" className="mb-8">
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Inventory</h3>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5 space-y-4 text-sm text-slate-600 dark:text-slate-400">
          <p>
            A visual grid editor for the player's inventory. Shows all inventory sections with
            Minecraft-style item icons.
          </p>

          <div>
            <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-2">Inventory Sections</h4>
            <ul className="space-y-2">
              <li><strong className="text-slate-800 dark:text-slate-200">Hotbar</strong> — The 9 slots in the player's hotbar (bottom row in-game).</li>
              <li><strong className="text-slate-800 dark:text-slate-200">Main Inventory</strong> — The 27 slots in the main inventory grid.</li>
              <li><strong className="text-slate-800 dark:text-slate-200">Armour</strong> — Helmet, chestplate, leggings, and boots slots.</li>
              <li><strong className="text-slate-800 dark:text-slate-200">Offhand</strong> — The offhand slot (used for shields, totems, etc.).</li>
              <li><strong className="text-slate-800 dark:text-slate-200">Ender Chest</strong> — The 27 slots in the player's ender chest.</li>
            </ul>
          </div>

          <div>
            <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-2">Editing Items</h4>
            <p className="mb-2">
              Click any inventory slot to add or change an item. The item editor enforces slot validity —
              for example, you can't place a non-armour item into a helmet slot.
            </p>
            <ul className="space-y-2">
              <li>
                <strong className="text-slate-800 dark:text-slate-200">Enchantments</strong> — Any
                enchantable item can have any valid enchantment applied. There is no level limit — you
                can create enchantments far beyond what's normally possible in the game (e.g. Sharpness 255).
              </li>
              <li>
                <strong className="text-slate-800 dark:text-slate-200">Custom Names</strong> — Give any
                item a custom display name.
              </li>
              <li>
                <strong className="text-slate-800 dark:text-slate-200">Durability</strong> — Repair
                durability damage on tools, weapons, and armour.
              </li>
              <li>
                <strong className="text-slate-800 dark:text-slate-200">Unbreakable</strong> — Tick
                the Unbreakable checkbox on any item with durability to make it never lose durability
                in-game. Unbreakable items show an "Unbreakable" tag in their inventory tooltip.
              </li>
              <li>
                <strong className="text-slate-800 dark:text-slate-200">Stack Size</strong> — Set the
                quantity of items in a stack.
              </li>
              <li>
                <strong className="text-slate-800 dark:text-slate-200">Armor Trims</strong> — Armor
                pieces show trim controls in the item editor. Choose from all 18 trim patterns (Bolt,
                Coast, Dune, Eye, Flow, etc.) and 11 materials (Amethyst, Copper, Diamond, Gold, etc.).
                Trimmed armor displays the pattern and material in its inventory tooltip, and trims
                are preserved when exporting and importing inventories.
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-2">Container Editing</h4>
            <p className="mb-2">
              Click any shulker box or bundle in a player's inventory to open a dedicated editor for
              its contents.
            </p>
            <ul className="space-y-2">
              <li>
                <strong className="text-slate-800 dark:text-slate-200">Shulker Boxes</strong> — Opens
                a 27-slot grid matching the in-game layout. Add, edit, and remove items inside just
                like regular inventory slots. You can also rename the shulker box from the editor.
              </li>
              <li>
                <strong className="text-slate-800 dark:text-slate-200">Bundles</strong> — Opens a
                16-slot grid with a live weight indicator showing how full the bundle is (capacity
                of 64). Items are rejected once the bundle reaches capacity. You can also rename
                the bundle from the editor.
              </li>
            </ul>
            <div className="rounded-lg border border-blue-200 dark:border-blue-800/50 bg-blue-50 dark:bg-blue-900/20 p-3 mt-3">
              <p className="text-blue-700 dark:text-blue-400 text-xs">
                Shulker boxes and bundles cannot be placed inside other containers, matching in-game
                rules. The editor will warn you if you try.
              </p>
            </div>
          </div>

          <div>
            <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-2">Inventory Actions</h4>
            <ul className="space-y-2">
              <li>
                <strong className="text-slate-800 dark:text-slate-200">Clear Inventory</strong> — Empty
                the player's entire inventory in one click.
              </li>
              <li>
                <strong className="text-slate-800 dark:text-slate-200">Export Inventory</strong> — Save
                the player's inventory to a JSON file. This can be used as a backup or to transfer an
                inventory between players or worlds.
              </li>
              <li>
                <strong className="text-slate-800 dark:text-slate-200">Import Inventory</strong> — Load
                a previously exported inventory file into any player on any world.
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Effects */}
      <div id="player-effects" className="mb-8">
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Effects</h3>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5 space-y-3 text-sm text-slate-600 dark:text-slate-400">
          <p>
            Add, remove, or modify status effects on a player. The interface is split into two panels:
          </p>
          <ul className="space-y-2">
            <li><strong className="text-slate-800 dark:text-slate-200">Available Effects</strong> (left) — A list of all Minecraft status effects with icons. Click one to select it, then configure the settings below and add it to the player.</li>
            <li><strong className="text-slate-800 dark:text-slate-200">Active Effects</strong> (right) — Effects currently applied to the player. You can modify their duration and level, or remove them.</li>
          </ul>
          <p>
            For each effect you can set the <strong>Duration</strong> (in seconds),
            <strong> Level</strong> (effect strength), and whether <strong>Particles</strong> are
            visible. Setting the duration to 0 applies the effect with infinite duration.
          </p>
        </div>
      </div>

      {/* Properties & Tags */}
      <div id="player-properties" className="mb-8">
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Properties & Tags</h3>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5 space-y-3 text-sm text-slate-600 dark:text-slate-400">
          <p>
            Manage dynamic properties and tags attached to a player. These are typically set by
            add-ons and behaviour packs.
          </p>
          <div>
            <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-2">Dynamic Properties</h4>
            <p>
              Displayed in a tree view grouped by the add-on that created them. You can double-click
              values to edit them, or select properties/groups and remove them. Useful for clearing
              data left behind by removed add-ons.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-2">Player Tags</h4>
            <p>
              A simple list of string tags on the player. You can add new tags or remove existing ones.
              Tags are commonly used by command systems and behaviour packs to track player states.
            </p>
          </div>
          <div className="rounded-lg border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/20 p-3">
            <p className="text-amber-700 dark:text-amber-400 text-xs">
              This is an advanced feature. Removing properties or tags used by active add-ons may cause
              unexpected behaviour in your world.
            </p>
          </div>
        </div>
      </div>

      {/* Advanced */}
      <div id="player-advanced" className="mb-8">
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Advanced</h3>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5 space-y-3 text-sm text-slate-600 dark:text-slate-400">
          <p>
            A raw NBT tree browser for the player's data, similar to the Level Editor's Advanced tab.
            Displays every NBT value stored for the player, allowing you to edit values that aren't
            exposed in the other tabs.
          </p>
          <p>
            Changes made here take priority over changes in other Player Editor tabs. If you edit the
            same field in both the General tab and the Advanced tab, the Advanced tab value wins.
          </p>
          <div className="rounded-lg border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/20 p-3">
            <p className="text-amber-700 dark:text-amber-400 text-xs">
              Power-user feature. Incorrect edits can corrupt player data. Use with caution.
            </p>
          </div>
        </div>
      </div>

    </section>
  );
}
